
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

interface VirtualScrollProps {
  items: any[];
  itemHeight: number | string;
  bufferSize: number;
  threshold: number;
  rootMargin: string;
  disabled: boolean;
  minItemsForVirtualization: number;
}

@Component
export default class VirtualScrollContainer extends Vue {
  @Prop({ required: true, type: Array })
  public items!: any[];

  @Prop({ default: 400, type: [Number, String] })
  public itemHeight!: number | string;

  @Prop({ default: 2, type: Number })
  public bufferSize!: number;

  @Prop({ default: 0.1, type: Number })
  public threshold!: number;

  @Prop({ default: '200px', type: String })
  public rootMargin!: string;

  @Prop({ default: false, type: Boolean })
  public disabled!: boolean;

  @Prop({ default: 5, type: Number })
  public minItemsForVirtualization!: number;

  @Prop({ default: false, type: Boolean })
  public useWindowScroll!: boolean;

  // Internal state
  private observer: IntersectionObserver | null = null;
  private scrollTop = 0;
  private containerHeight = 0;
  private isLoading = false;
  private visibleStartIndex = 0;
  private visibleEndIndex = 0;

  // Computed properties
  get shouldUseVirtualScrolling(): boolean {
    return !this.disabled && this.items.length >= this.minItemsForVirtualization;
  }

  get itemHeightValue(): number {
    return typeof this.itemHeight === 'number' ? this.itemHeight : 400;
  }

  get startIndex(): number {
    if (!this.shouldUseVirtualScrolling) return 0;
    return Math.max(0, this.visibleStartIndex - this.bufferSize);
  }

  get endIndex(): number {
    if (!this.shouldUseVirtualScrolling) return this.items.length;
    return Math.min(this.items.length, this.visibleEndIndex + this.bufferSize);
  }

  get visibleItems(): any[] {
    if (!this.shouldUseVirtualScrolling) return this.items;
    return this.items.slice(this.startIndex, this.endIndex);
  }

  get beforeSpacerHeight(): number {
    if (!this.shouldUseVirtualScrolling) return 0;
    return this.startIndex * this.itemHeightValue;
  }

  get afterSpacerHeight(): number {
    if (!this.shouldUseVirtualScrolling) return 0;
    return (this.items.length - this.endIndex) * this.itemHeightValue;
  }

  get totalHeight(): number {
    return this.items.length * this.itemHeightValue;
  }

  // Lifecycle hooks
  public mounted(): void {
    this.initializeVirtualScrolling();
    if (this.useWindowScroll) {
      this.setupWindowScrollListener();
    }
  }

  public beforeDestroy(): void {
    this.cleanup();
    if (this.useWindowScroll) {
      this.removeWindowScrollListener();
    }
  }

  // Watch for items changes
  @Watch('items')
  private onItemsChange(): void {
    this.recalculateVisibleRange();
  }

  @Watch('itemHeight')
  private onItemHeightChange(): void {
    this.recalculateVisibleRange();
  }

  // Methods
  private initializeVirtualScrolling(): void {
    if (!this.shouldUseVirtualScrolling) return;

    this.updateContainerHeight();
    this.recalculateVisibleRange();
    this.setupIntersectionObserver();
  }

  private updateContainerHeight(): void {
    if (this.useWindowScroll) {
      this.containerHeight = window.innerHeight;
    } else {
      const container = this.$refs.scrollContainer as HTMLElement;
      if (container) {
        this.containerHeight = container.clientHeight;
      }
    }
  }

  private recalculateVisibleRange(): void {
    if (!this.shouldUseVirtualScrolling) return;

    if (this.useWindowScroll) {
      // Use window scroll position
      const container = this.$refs.scrollContainer as HTMLElement;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const containerTop = window.pageYOffset - containerRect.top;
      
      // Calculate how much of the container has scrolled past the top of the viewport
      this.scrollTop = Math.max(0, containerTop);
      this.containerHeight = window.innerHeight;
    } else {
      // Use container scroll position
      const container = this.$refs.scrollContainer as HTMLElement;
      if (!container) return;
      
      this.scrollTop = container.scrollTop;
      this.containerHeight = container.clientHeight;
    }

    // Calculate visible range
    this.visibleStartIndex = Math.floor(this.scrollTop / this.itemHeightValue);
    this.visibleEndIndex = Math.min(
      this.items.length,
      Math.ceil((this.scrollTop + this.containerHeight) / this.itemHeightValue)
    );
  }

  private handleScroll(): void {
    if (!this.shouldUseVirtualScrolling) return;

    // Use requestAnimationFrame to throttle scroll events
    requestAnimationFrame(() => {
      this.recalculateVisibleRange();
    });
  }

  private handleWindowScroll(): void {
    if (!this.shouldUseVirtualScrolling) return;

    // Use requestAnimationFrame to throttle scroll events
    requestAnimationFrame(() => {
      this.recalculateVisibleRange();
    });
  }

  private setupWindowScrollListener(): void {
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
    window.addEventListener('resize', this.recalculateVisibleRange, { passive: true });
  }

  private removeWindowScrollListener(): void {
    window.removeEventListener('scroll', this.handleWindowScroll);
    window.removeEventListener('resize', this.recalculateVisibleRange);
  }

  private setupIntersectionObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('IntersectionObserver not supported, falling back to regular rendering');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const itemIndex = this.getItemIndexFromElement(element);
            if (itemIndex >= 0) {
              this.loadItemData(itemIndex);
            }
          }
        });
      },
      {
        root: this.useWindowScroll ? null : (this.$refs.scrollContainer as Element),
        rootMargin: this.rootMargin,
        threshold: this.threshold,
      }
    );

    // Observe initially visible items
    this.$nextTick(() => {
      this.observeVisibleItems();
    });
  }

  private observeVisibleItems(): void {
    if (!this.observer) return;

    // Observe all currently rendered items
    const itemRefs = this.$refs;
    Object.keys(itemRefs).forEach((refKey) => {
      if (refKey.startsWith('item-')) {
        const elements = itemRefs[refKey] as HTMLElement | HTMLElement[];
        const elementArray = Array.isArray(elements) ? elements : [elements];
        elementArray.forEach((element) => {
          if (element) {
            this.observer!.observe(element);
          }
        });
      }
    });
  }

  private getItemIndexFromElement(element: HTMLElement): number {
    const refKey = Object.keys(this.$refs).find((key) => {
      const ref = this.$refs[key];
      return (Array.isArray(ref) ? ref : [ref]).includes(element);
    });

    if (refKey && refKey.startsWith('item-')) {
      return parseInt(refKey.replace('item-', ''), 10);
    }

    return -1;
  }

  public isItemVisible(index: number): boolean {
    return index >= this.visibleStartIndex && index <= this.visibleEndIndex;
  }

  public loadItemData(index: number): void {
    if (index < 0 || index >= this.items.length) return;

    this.isLoading = true;

    // Emit event to parent component to handle data loading
    this.$emit('item-load', {
      item: this.items[index],
      index,
      callback: () => {
        this.isLoading = false;
      },
    });

    // For immediate feedback, mark as loaded after a short delay
    setTimeout(() => {
      this.isLoading = false;
    }, 100);
  }

  private cleanup(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  // Public methods for external control
  public scrollToItem(index: number): void {
    if (index < 0 || index >= this.items.length) return;

    const container = this.$refs.scrollContainer as HTMLElement;
    if (container) {
      const targetScrollTop = index * this.itemHeightValue;
      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    }
  }

  public scrollToTop(): void {
    const container = this.$refs.scrollContainer as HTMLElement;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  public refreshItems(): void {
    this.recalculateVisibleRange();
    this.$nextTick(() => {
      this.observeVisibleItems();
    });
  }
}
